<template>
  <div class="tags-container" style="width: 100%;">
    <select class="single selectize selectize-multiple selectize-bordered selectized" :name="selectedTagsInputName" multiple></select>
    <div v-if="otherTags && otherTags.length" style="padding-top: 10px;">
      <h3>Other Details</h3>
      <p style="padding-bottom: 15px;">Please provide additional details for 'other' selections</p>
      <div v-for="tag in value" v-if="tag.is_other_option" :key="tag.id" style="padding-bottom: 15px;">
        <p style="font-weight: 600; padding-bottom: 2px;">{{ tag.database_name }}{{ tag.is_other_option ? ' - ' +
          tag.type : '' | humanize }}{{ tag.appearance_type ? ' - ' + tag.appearance_type : '' | humanize }}:</p>
        <input style="min-width: 300px;" v-model="tag.other_text">
      </div>
    </div>
  </div>
</template>

<style scoped>
  .tags {
    margin-top: 20px;
  }
  .selectize-tags.selectize-control .selectize-input:after {
    position: absolute;
    cursor: pointer;
    background-color: #1a1a1a;
    width: 46px;
    height: 46px;
    top: 0;
    right: 0;
    margin: 0;
    border: none;
    content: "\f067";
    font-family: FontAwesome;
    color: #fff;
    font-size: 20px;
    text-align: center;
    padding: 11px 0;
  }
</style>

<script>
import 'selectize'
import { each, map, find, filter, trim, assign } from 'lodash'
import $ from 'jquery'
import { mapActions, mapState } from 'vuex'

export default {
  props: {
    value: {
      required: false
    },
    type: {
      default: ''
    },
    types: {
      required: true,
      type: Array
    },
    id: {
      required: false,
      default: null
    },
    booking: {
      required: true,
      type: Object
    },
    propertyMutator: {
      required: true,
      type: Function
    }
  },

  data () {
    return {
      selectize: null,
      selectedTags: [],
      availableTags: []
    }
  },

  watch: {
    /**
     * Watch changes to the selected tags so we can emit it on the built-in "input" event
     * which should push changes to any v-models.
     *
     * @return void
     */
    selectedTags () {
      this.$emit('input', this.selectedTags)
    }
  },

  /**
   * On mounted we bind Selectize to the input and add any pre-selected tags.
   *
   * @return void
   */
  mounted () {
    this.bind()

    this.addPreSelected()
  },

  computed: {
    ...mapState('editing', ['editing']),

    otherTags () {
      if (this.value) {
        return this.value.filter(element => element.is_other_option)
      }
      return []
    },

    localField: {
      get () {
        return this.booking[this.field]
      },
      set (value) {
        this.propertyMutator({
          property: this.field,
          value
        })
      }
    },
    /**
     * Determine if we have selected tags.
     *
     * @return bool
     */
    haveSelectedTags () {
      return this.selectedTags.length > 0
    },

    /**
     * The input field name for the selected tags.
     *
     * @return string
     */
    selectedTagsInputName () {
      if (this.type) {
        return `${this.type}_tags[]`
      }

      return 'tags[]'
    }
  },

  methods: {
    ...mapActions('booking/request', ['setRequestProperty']),
    ...mapActions('booking', ['setBookingProperty']),
    /**
     * Bind Selectize to the DOM.
     *
     * @return void
     */
    bind () {
      this.selectize = $(this.$el).find('select').selectize({
        plugins: ['remove_button'],
        options: map(this.value, this.mapTagResponseForSelectize),
        preload: true,
        persist: true,
        valueField: 'id',
        labelField: 'name',
        searchField: 'name',
        /**
         * Filter out tags with exact matches for both the upper/lowercase versions.
         *
         * @return bool
         */
        createFilter: (tag) => {
          tag = trim(tag)

          // Compare the lowercase of existing available tags against the search term when creating
          // tags.
          if (find(this.availableTags, existing => existing.name.toLowerCase() === tag.toLowerCase()) !== undefined) {
            return false
          }

          // Do the same against the selected tags so the user can't type out a lower case selected
          // tag and add it to the database.
          if (find(this.selectedTags, selected => selected.name.toLowerCase() === tag.toLowerCase()) !== undefined) {
            return false
          }

          return true
        },
        /**
         * When an item is added when make sure it's in the array of available tags.
         * This then adds it to the selected tags so we can render them outside of Selectize.
         *
         * @return void
         */
        onItemAdd: (value) => {
          let tag = find(this.availableTags, (tag) => tag.id === parseInt(value))

          if (tag) {
            this.addSelectedTag(tag)
          }
        },
        /**
         * When an item is removed, trigger remove tag method so that changes will be emitted to parent component
         *
         * @return void
         */
        onItemRemove: value => {
          let tag = find(this.selectedTags, tag => tag.id === parseInt(value))

          if (tag) {
            this.removeTag(tag)
          }
        },
        /**
         * When typing into the Selectize field we'll poll the server for results. The results
         * are then assigned to the available tags array.
         *
         * @return void
         */
        load: (term, callback) => {
          let params = {
            types: this.types
          }

          this.$http.get('opportunities/show', { params })
            .then(response => {
              let tags = map(response.data, this.mapTagResponseForSelectize)
              assign(this.availableTags, tags)
              callback(tags)
            })
        },
        onChange: () => {
          this.selectize.$dropdown_content.removeHighlight()
        },
        onType: (string) => {
          if (!string) {
            this.selectize.$dropdown_content.removeHighlight()
          }
        },
        onDelete: () => {
          setTimeout(() => {
            this.selectize.$dropdown_content.removeHighlight()
          }, 0)
        }
      })[0].selectize
    },

    /**
     * Map a tag response to a format Selectize expects.
     *
     * @return object
     */
    mapTagResponseForSelectize (tag) {
      let name = tag.name
      if (name === 'Other') {
        if (tag.other_text) {
          name = `${tag.name} (${tag.type}, ${tag.appearance_type}) - ${tag.other_text}`
        } else {
          name = `${tag.name} (${tag.type}, ${tag.appearance_type})`
        }
      }
      return {...tag, name}
    },

    /**
     * Add the pre-selected tags to both available tags and selected tags.
     *
     * @return void
     */
    addPreSelected () {
      each(map(this.value, this.mapTagResponseForSelectize), (tag) => {
        if (!this.tagAlreadyAvailable(tag)) {
          this.addAvailableTag(tag)
        }

        if (!this.tagAlreadySelected(tag)) {
          this.addSelectedTag(tag)
        }
      })
    },

    /**
     * Determine if a tag is already selected. Tag can be an integer or object.
     *
     * @param int|object tag
     *
     * @return bool
     */
    tagAlreadySelected (tag) {
      let id = tag.id || parseInt(tag)

      return find(this.selectedTags, (existing) => existing.id === id) !== undefined
    },

    /**
     * Determine if a tag is already available. Tag can be an integer or object.
     *
     * @param int|object tag
     *
     * @return bool
     */
    tagAlreadyAvailable (tag) {
      let id = tag.id || parseInt(tag)

      return find(this.availableTags, (existing) => existing.id === id) !== undefined
    },

    /**
     * Add a new selected tag.
     *
     * @param object tag
     */
    addSelectedTag (tag) {
      if (!this.tagAlreadySelected(tag)) {
        this.selectedTags.push(tag)

        this.selectize.addItem(tag.id)
      }
    },

    /**
     * Add a new available tag.
     *
     * @param object tag
     */
    addAvailableTag (tag) {
      if (!this.tagAlreadyAvailable(tag)) {
        this.availableTags.push(tag)
      }
    },

    /**
     * Remove a tag from the selected tags.
     *
     * @param object tag
     *
     * @return void
     */
    removeTag (tag) {
      this.selectedTags = filter(this.selectedTags, existing => existing.id !== tag.id)

      this.selectize.removeItem(tag.id)
    }
  }
}
</script>
